import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { api, setUnauthorisedHandler, tokenStore, type AccountSummary } from '@/api/client';

interface AuthState {
  account: AccountSummary | null;
  /** True until the stored token has been checked, so nothing flashes. */
  checking: boolean;
  signIn: (email: string, password: string) => Promise<AccountSummary>;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<AccountSummary | null>(null);
  const [checking, setChecking] = useState(true);

  const signOut = useCallback(() => {
    tokenStore.clear();
    setAccount(null);
  }, []);

  useEffect(() => {
    // A 401 from anywhere drops straight back to the sign-in screen. Without
    // this an expired token leaves every table showing the same error and no
    // way out of it.
    setUnauthorisedHandler(() => setAccount(null));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const token = tokenStore.get();
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .me()
      .then((me) => {
        if (cancelled) return;
        setAccount(me.is_super_admin ? me : null);
        if (!me.is_super_admin) tokenStore.clear();
      })
      .catch(() => {
        if (!cancelled) tokenStore.clear();
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await api.login(email, password);
    tokenStore.set(result.access_token);
    if (!result.user.is_super_admin) {
      // Correct credentials, wrong door. Clearing the token keeps a
      // non-admin from carrying a valid session around this app.
      tokenStore.clear();
      throw new NotAnAdminError();
    }
    setAccount(result.user);
    return result.user;
  }, []);

  const value = useMemo(
    () => ({ account, checking, signIn, signOut }),
    [account, checking, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export class NotAnAdminError extends Error {
  constructor() {
    super('not an admin');
    this.name = 'NotAnAdminError';
  }
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
